import { supabase, run } from './db';
import { cached } from './local/cache';
import { queueable } from './local/queued';
import { atualizarSeIntacto } from './conflitos';
import { todosOsNomes } from '../utils/equipe';

/** Serviços do laboratório, mais recentes primeiro. */
export const listServices = (laboratoryId) =>
  cached(`services:${laboratoryId}`, () => run(supabase
    .from('services')
    .select('*')
    .eq('laboratory_id', laboratoryId)
    .order('date', { ascending: false })));

/**
 * Cria serviço com id gerado no aparelho.
 *
 * O id vem do cliente, não do `gen_random_uuid()` do banco, porque um item da
 * fila pode ser reenviado: sem id fixo, a segunda tentativa criaria um serviço
 * duplicado em vez de reconhecer que o primeiro já entrou. Com id fixo, o
 * banco devolve violação de chave e o envio trata como "já aplicado".
 */
export const insertService = queueable('service.insert',
  (row) => run(supabase.from('services').insert([row])),
  {
    patchOf: (row) => ({
      chave: `services:${row.laboratory_id}`,
      tipo: 'insere',
      linha: row,
    }),
  });

export const newServiceId = () => crypto.randomUUID();

/**
 * Atualização genérica de serviço.
 *
 * Vários fluxos diferentes escrevem aqui — cadastro, faturamento, pagamento,
 * custos e o resumo do workflow. Ficam como funções separadas abaixo porque a
 * fila offline precisa distinguir "faturei" de "editei o cadastro": são
 * intenções diferentes, ainda que a tabela seja a mesma.
 */
// `carimbo` é o `updated_at` que o aparelho conhecia quando o usuário abriu o
// registro. A gravação só passa se ele continuar valendo — senão alguém editou
// no meio, e o lançamento é retido em vez de sobrescrever.
const patch = (serviceId, updates, _laboratoryId, carimbo) =>
  atualizarSeIntacto('services', serviceId, updates, carimbo);

// Cada intenção tem nome próprio na fila. Todas são o mesmo UPDATE por id —
// idempotente, então reenviar é seguro —, mas o nome é o que permite dizer ao
// usuário o que está pendente: "faturamento de 3 serviços" e não "3 updates".
//
// O terceiro argumento (`laboratoryId`) não é usado na escrita: serve só para
// montar a chave da emenda, que é o que mantém a alteração visível na tela
// enquanto o item não sobe.
const emendaDeServico = (serviceId, updates, laboratoryId) => (laboratoryId ? {
  chave: `services:${laboratoryId}`,
  tipo: 'atualiza',
  id: serviceId,
  campos: updates,
} : null);

const comEmenda = (nome) => queueable(nome, patch, { patchOf: emendaDeServico });

export const updateService = comEmenda('service.update');
export const updateServiceBilling = comEmenda('service.billing');

/**
 * NÃO REMOVER, ainda que nenhuma tela chame.
 *
 * O pagamento passou a ser por parcela (`data/invoices.js`), e `services.paid`
 * virou reflexo mantido por gatilho. Mas esta função continua REGISTRADA na
 * fila offline sob o nome 'service.payment': um aparelho que ficou sem sinal
 * antes da mudança pode ter itens com esse nome esperando para subir, e
 * `resolveOp('service.payment')` precisa achar alguém do outro lado.
 *
 * Some quando não houver mais fila antiga em campo.
 */
export const updateServicePayment = comEmenda('service.payment');
export const updateServiceCosts = comEmenda('service.costs');
// Anotação de quem cobra ("vai pagar dia 15"). Nome próprio na fila pelo mesmo
// motivo dos outros: o pendente diz "observação de cobrança", não "update".
export const updateServiceObservacaoDeCobranca = comEmenda('service.observacao_de_cobranca');
export const updateServiceWorkflow = comEmenda('service.workflow');

export const deleteService = queueable('service.delete',
  (serviceId) => run(supabase.from('services').delete().eq('id', serviceId)),
  {
    patchOf: (serviceId, laboratoryId) => (laboratoryId ? {
      chave: `services:${laboratoryId}`, tipo: 'remove', id: serviceId,
    } : null),
  });

/**
 * Filtra serviços pelo que a tela oferece: período, busca e SEBRAE.
 *
 * A DATA OLHADA É `service.date` — a data do serviço, não a de cada etapa.
 * Filtrar por etapa responderia "o que aconteceu no período"; esta tela
 * responde "quais contratos são do período", que é a pergunta de quem procura
 * um serviço para abrir.
 *
 * `de` e `ate` são inclusivos nos dois extremos. Um intervalo que exclui o
 * último dia é a origem clássica do "cadê o serviço de ontem".
 *
 * A busca cobre os três nomes mais o código: quem procura "Nezinho" quer o
 * serviço da fazenda dele, quem procura "#41" quer aquele número, e quem
 * digita "Conceição" quer o mesmo serviço pelo outro nome.
 */
export const filtrarServicos = (services = [], filtros = {}, identidade) => {
  const { de, ate, busca, somenteSebrae, sebraeProjeto } = filtros;
  const q = (busca || '').trim().toLowerCase();
  const sp = (sebraeProjeto || '').trim().toLowerCase();

  return (services || []).filter(s => {
    const data = String(s.date || '').slice(0, 10);
    if (de && (!data || data < de)) return false;
    if (ate && (!data || data > ate)) return false;

    if (somenteSebrae && !s.sebrae) return false;
    if (sp && !String(s.sebrae_projeto || '').toLowerCase().includes(sp)) return false;

    if (q) {
      const id = identidade ? identidade(s) : { principal: s.client_name, secundario: '' };
      const campos = [
        s.client_name, s.service_code, s.numero, s.service_label,
        id?.principal, id?.secundario,
        // Quem vai às etapas: "o que o Breno tem esta semana?" é busca.
        ...todosOsNomes(s.equipe_das_etapas),
      ];
      if (!campos.some(v => String(v || '').toLowerCase().includes(q))) return false;
    }

    return true;
  });
};
